import React from 'react';
import './turnover-overview-tab.scss';
import { ReactComponent as EditIcon } from '@zonofi/svg-icons/edit.svg';
import { ReactComponent as PhoneIcon } from '@zonofi/svg-icons/phone-icon.svg';
import { ReactComponent as ArrowRightIcon } from '@zonofi/svg-icons/arrow_right.svg';
import { ReactComponent as EmailIcon } from '@zonofi/svg-icons/envelope.svg';
import { ReactComponent as LocationIcon } from '@zonofi/svg-icons/location-pin-icon.svg';
import { ReactComponent as SuccessIcon } from '@zonofi/svg-icons/success-tick.svg';
import { ReactComponent as CheckMarkIcon } from '@zonofi/svg-icons/check-mark.svg';
import { ReactComponent as RefreshIcon } from '@zonofi/svg-icons/refresh.svg';
import { ReactComponent as ClockIcon } from '@zonofi/svg-icons/clock.svg';
import { ReactComponent as CloseIcon } from '@zonofi/svg-icons/cross-icon.svg';
import { ReactComponent as NewTabLinkIcon } from '@zonofi/svg-icons/new-tab-link.svg';

import { Avatar } from '@zonofi/seller-hub/components';
import { TurnoverInvoiceOverviewData } from '@zonofi/zono-money-store';

interface InfoCardProps {
  avatarText: string;
  label: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  gst: string;
  onRemap?: () => void;
}

const EntityCard: React.FC<InfoCardProps> = ({
  avatarText,
  label,
  name,
  phone,
  email,
  address,
  gst,
  onRemap,
}) => (
  <div className="tov-card entity-card">
    <div className="entity-card__header">
      <Avatar
        id={avatarText}
        title={name}
        size={36}
        style={{ borderRadius: '4px' }}
      />
      <div>
        <span className="tov-label font-semibold">{label}</span>
        <span className="entity-card__name font-semibold">{name}</span>
      </div>
    </div>
    <div className="entity-card__meta">
      <div className="entity-card__meta-row">
        <div className="entity-card__meta-row-item">
          <span className="meta-icon">
            <PhoneIcon color="#8C8C8C" height={16} width={16} />
          </span>
          <span className="font-regular">{phone}</span>
        </div>
        <div className="entity-card__meta-row-item-divider"></div>
        <div className="entity-card__meta-row-item">
          <span className="meta-icon">
            <EmailIcon color="#8C8C8C" height={16} width={16} />
          </span>
          <span className="font-regular">{email}</span>
        </div>
      </div>
      <div className="entity-card__meta-row">
        <div className="entity-card__meta-row-item">
          <span className="meta-icon">
            <LocationIcon color="#8C8C8C" height={16} width={16} />
          </span>
          <span className="font-regular">{address}</span>
        </div>
      </div>
    </div>
    <div className="entity-card__footer">
      <div className="entity-card-left-content">
        <div className="gst-badge">
          <SuccessIcon height={12} width={12} color="#186b48" />
          <span className="font-regular">{gst}</span>
        </div>
        <CheckMarkIcon height={8} width={8} />
      </div>
      <button className="remap-btn" onClick={onRemap}>
        <RefreshIcon height={16} width={16} />
        Remap
      </button>
    </div>
  </div>
);

export interface TurnoverOverviewTabProps {
  data: TurnoverInvoiceOverviewData;
  onOpenInvoice?: () => void;
}

const getAvatarId = (value: string) =>
  value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || '--';

const formatCurrency = (amount: number) =>
  `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const TurnoverOverviewTab: React.FC<TurnoverOverviewTabProps> = ({
  data,
  onOpenInvoice,
}) => {
  return (
    <div className="tov-root">
      <div className="tov-scroll-content">
        <div className="tov-values-row">
          <div className="tov-card tov-value-card">
            <span className="tov-label font-semibold">Total Invoice Value</span>
            <div className="tov-value-row">
              <span className="tov-amount font-semibold">
                {formatCurrency(data.totalInvoiceValue)}
              </span>
              <button className="tov-edit-btn" disabled>
                <EditIcon height={20} width={20} />
              </button>
            </div>
          </div>
          <div className="tov-card tov-value-card">
            <span className="tov-label font-semibold">Our Products Value</span>
            <span className="tov-amount font-semibold">
              {formatCurrency(data.ourProductsValue)}
            </span>
          </div>
        </div>

        <div className="tov-card contribution-card">
          <div className="contribution-card__row">
            <span className="tov-label font-semibold">Contribution Value</span>
            <span className="tov-amount font-semibold">
              {formatCurrency(data.contributionValue)}
            </span>
          </div>
          <button className="more-details-btn font-semibold">
            More Details
            <ArrowRightIcon height={16} width={16} color="#0067ff" />
          </button>
        </div>

        <EntityCard
          label="Distributor/CFA"
          avatarText={getAvatarId(data.distributor.name)}
          name={data.distributor.name}
          phone={data.distributor.phone}
          email={data.distributor.email}
          address={data.distributor.address}
          gst={data.distributor.gst}
          onRemap={() => {}}
        />

        <EntityCard
          label="Buyer"
          avatarText={getAvatarId(data.buyer.name)}
          name={data.buyer.name}
          phone={data.buyer.phone}
          email={data.buyer.email}
          address={data.buyer.address}
          gst={data.buyer.gst}
          onRemap={() => {}}
        />

        <div className="tov-card submitted-card">
          <div className="submitted-card__header">
            <Avatar
              id={getAvatarId(data.submittedByName)}
              title={data.submittedByName}
              size={36}
              style={{ borderRadius: '4px' }}
            />
            <div className="submitted-card__info">
              <span className="tov-label font-semibold">Submitted by</span>
              <span className="entity-card__name font-semibold">
                {data.submittedByName}
              </span>
            </div>
          </div>
          <div className="submitted-card__meta">
            <div className="submitted-card__meta-item">
              <ClockIcon height={16} width={16} color="#8C8C8C" />
              <span className="font-regular">{data.submittedOn}</span>
            </div>

            <button
              className="view-invoice-btn"
              onClick={onOpenInvoice}
              disabled={!data.invoiceUrl}
            >
              View Invoice <NewTabLinkIcon height={16} width={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Action Buttons */}
      <div className="tov-actions">
        <button className="tov-btn tov-btn--reject font-semibold">
          Reject <CloseIcon height={16} width={16} color="#fff" />
        </button>
        <button className="tov-btn tov-btn--accept font-semibold">
          Accept <ArrowRightIcon height={16} width={16} color="#fff" />
        </button>
      </div>
    </div>
  );
};
